import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Settings as SettingsIcon, History, Save, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import CatalogManager from "../components/CatalogManager";
import FieldCatalogManager from "../components/FieldCatalogManager";

const ACTION_LABELS = {
  creado: "Creó el desarrollo",
  cambio_prioridad: "Cambió la prioridad",
  cambio_estado: "Cambió el estado",
  correccion_agregada: "Agregó una corrección",
  eliminado: "Eliminó el desarrollo",
  subtarea_agregada: "Agregó una subtarea",
  subtarea_editada: "Editó una subtarea",
  subtarea_eliminada: "Eliminó una subtarea",
  subtarea_completada: "Completó una subtarea",
  subtarea_reabierta: "Reabrió una subtarea",
};

export default function Settings() {
  const { isPM } = useAuth();
  const [settings, setSettings] = useState({ stale_days: 3, max_active_tasks: 5, due_soon_days: 2 });
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [runningCheck, setRunningCheck] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/audit").then((r) => setAudit(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        stale_days: Number(settings.stale_days),
        max_active_tasks: Number(settings.max_active_tasks),
        due_soon_days: Number(settings.due_soon_days),
      });
      toast.success("Umbrales actualizados. El tablero se reevaluará.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Ingresa un correo destino"); return; }
    setSendingTest(true);
    try {
      const { data } = await api.post("/notifications/test", { to: testEmail.trim(), sandbox: false });
      toast.success(`Correo enviado a ${testEmail} (Mailjet)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingTest(false);
    }
  };

  const runDueCheck = async () => {
    setRunningCheck(true);
    try {
      await api.post("/notifications/run-due-check");
      toast.success("Chequeo de vencimientos ejecutado. Correos enviados a los responsables/PM.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRunningCheck(false);
    }
  };

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Sistema</p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2"><SettingsIcon className="h-7 w-7" />Configuración</h1>
      </div>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Thresholds */}
        <div className="border border-border bg-white rounded-sm p-6">
          <h3 className="font-heading font-bold text-lg mb-1">Umbrales del motor de reglas</h3>
          <p className="text-sm text-slate-500 mb-5">Configura cuándo el sistema genera alertas automáticas.</p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Días sin movimiento para marcar "estancado"</Label>
              <Input data-testid="stale-days-input" type="number" min={1} value={settings.stale_days} disabled={!isPM}
                onChange={(e) => setSettings((s) => ({ ...s, stale_days: e.target.value }))} className="mt-1.5 rounded-sm font-mono w-32" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Máx. tareas activas por desarrollador</Label>
              <Input data-testid="max-tasks-input" type="number" min={1} value={settings.max_active_tasks} disabled={!isPM}
                onChange={(e) => setSettings((s) => ({ ...s, max_active_tasks: e.target.value }))} className="mt-1.5 rounded-sm font-mono w-32" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Días de anticipación para "próximo a vencer"</Label>
              <Input data-testid="due-soon-days-input" type="number" min={1} value={settings.due_soon_days} disabled={!isPM}
                onChange={(e) => setSettings((s) => ({ ...s, due_soon_days: e.target.value }))} className="mt-1.5 rounded-sm font-mono w-32" />
              <p className="text-[11px] text-slate-400 mt-1">Envía correo automático cuando falten estos días o menos.</p>
            </div>
          </div>
          {isPM ? (
            <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="mt-6 rounded-sm bg-slate-900 hover:bg-slate-800">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          ) : (
            <p className="mt-6 text-xs text-slate-400">Solo el PM puede modificar los umbrales.</p>
          )}
        </div>

        {/* Audit log */}
        <div className="border border-border bg-white rounded-sm p-6">
          <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><History className="h-5 w-5" />Historial / Auditoría</h3>
          <p className="text-sm text-slate-500 mb-4">Cambios de prioridad y estado por usuario.</p>
          <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="audit-log">
            {audit.map((a, i) => (
              <div key={`${a.timestamp}-${a.dev_id}-${a.action}-${i}`} className="flex items-start gap-3 border-b border-slate-100 pb-2 last:border-0">
                <div className="mt-1 h-2 w-2 rounded-full bg-slate-900 shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold">{a.user_name}</span> — {ACTION_LABELS[a.action] || a.action}
                  {a.old && a.new && <span className="text-slate-500"> de <span className="font-mono text-xs">{a.old}</span> a <span className="font-mono text-xs">{a.new}</span></span>}
                  <div className="text-xs text-slate-400 mt-0.5">
                    <span className="truncate">{a.dev_title}</span> · {new Date(a.timestamp).toLocaleString("es-ES")}
                  </div>
                </div>
              </div>
            ))}
            {audit.length === 0 && <p className="text-slate-400 text-sm">Sin registros aún.</p>}
          </div>
        </div>
      </div>

      {isPM && (
        <div className="px-8 pb-8">
          <div className="border border-border bg-white rounded-sm p-6" data-testid="email-config">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Mail className="h-5 w-5" />Notificaciones por Email</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Envío activo vía <strong>Mailjet</strong> — remitente <span className="font-mono text-xs">no-reply@tomza.com</span>. Las alertas críticas (vencimientos, correcciones, cambios a producción) se envían automáticamente al responsable y al PM.
            </p>
            <div className="flex items-end gap-2 max-w-md">
              <div className="flex-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Enviar correo de prueba a</Label>
                <Input data-testid="test-email-input" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                  className="mt-1.5 rounded-sm" placeholder="destino@correo.com" />
              </div>
              <Button data-testid="send-test-email-btn" onClick={sendTest} disabled={sendingTest} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-10">
                <Send className="h-4 w-4 mr-1.5" /> {sendingTest ? "Enviando..." : "Enviar"}
              </Button>
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-2">Las alertas de <strong>próximo a vencer</strong> y <strong>vencido</strong> se envían automáticamente cada hora. Puedes ejecutarlas ahora:</p>
              <Button data-testid="run-due-check-btn" variant="outline" onClick={runDueCheck} disabled={runningCheck} className="rounded-sm h-9">
                {runningCheck ? "Ejecutando..." : "Ejecutar chequeo de vencimientos ahora"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isPM && (
        <div className="px-8 pb-8">
          <CatalogManager />
          <FieldCatalogManager />
        </div>
      )}
    </div>
  );
}
